import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { adminService } from "@/services/adminService";
import { apiError } from "@/lib/api";
import { Plus, Archive, ArrowRight, Check, X, Clock, RotateCcw, RotateCw, Loader2, Search, MoreVertical, Pencil, Paperclip, Save, Download, ArrowLeft, FileText, Trash2, Info, Coins, Hammer, Settings2, AlertTriangle, ExternalLink, Copy, GripHorizontal } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

const CHARCOAL = "#2A2A28";
const LINE = "rgba(42,42,40,0.16)";
const MUTE = "rgba(42,42,40,0.55)";

const STAGES = [
  { id: "new", label: "1 · New" },
  { id: "analysis", label: "2 · Analysis" },
  { id: "triage", label: "3 · Triage" },
  { id: "build", label: "4 · Build/Test" },
  { id: "uat", label: "5 · UAT & Deploy" },
  { id: "closed", label: "6 · Closed" },
];

const ANALYSIS_BEST_PRACTICES = [
  "AS-IS / TO-BE",
  "Functional Design Concept",
  "Dependencies",
  "Cost",
  "Implement Impact",
  "Required Admin functions",
  "Compliance",
  "Benefits vs Risks",
  "Risk Mitigation",
];

const ARCHIVE_GROUPS = [
  { id: "in_production", label: "In Production" },
  { id: "future", label: "Future Consideration" },
  { id: "rejected", label: "Rejected" },
];

function Card({ card, menu, selectable, selected, onToggleSelect, onRequestBuild, requestKind }) {
  const requested = !!card.build_requested;
  const isAnalysis = requestKind === "analysis";
  return (
    <div
      className="rounded-md border p-2.5 mb-2"
      style={{
        borderColor: requested ? "#3f9142" : LINE,
        background: requested ? "rgba(63,145,66,0.08)" : "rgba(255,255,255,0.7)",
        ...(selected ? { outline: `2px solid ${CHARCOAL}`, outlineOffset: "-1px" } : {}),
      }}
      data-testid={`change-card-${card.id}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex items-start gap-2 min-w-0">
          {selectable && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={onToggleSelect}
              data-testid={`archive-select-${card.id}`}
              aria-label={`Select ${card.title}`}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer"
              style={{ accentColor: CHARCOAL }}
            />
          )}
          <div className="min-w-0">
            {card.cr_number != null && (
              <span className="block text-[9px] font-bold tracking-wide" style={{ color: MUTE }} data-testid={`change-crno-${card.id}`}>
                CR-{String(card.cr_number).padStart(3, "0")}
              </span>
            )}
            <p className="text-xs font-semibold leading-snug" style={{ color: CHARCOAL }}>{card.title}</p>
          </div>
        </div>
        {menu}
      </div>
      {card.originated_by && (
        <span className="mt-1.5 inline-block text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ color: CHARCOAL, background: "rgba(42,42,40,0.08)" }} data-testid={`change-origin-${card.id}`}>
          {card.originated_by}
        </span>
      )}
      {card.type && (
        <span className="mt-1.5 ml-1 inline-block text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ color: "#3730a3", background: "rgba(99,102,241,0.14)" }} data-testid={`change-type-${card.id}`}>
          {card.type}
        </span>
      )}
      {card.priority && (
        <span className="mt-1.5 ml-1 inline-block text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ color: "#9a3412", background: "rgba(234,88,12,0.14)" }} data-testid={`change-priority-${card.id}`}>
          {card.priority}
        </span>
      )}
      {card.planned_cost != null && (
        <span className="mt-1.5 ml-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ color: "#8a6d1b", background: "rgba(198,161,91,0.16)" }} data-testid={`change-cost-${card.id}`}>
          <Coins className="h-2.5 w-2.5" /> {card.planned_cost} ECU
        </span>
      )}
      {card.actual_cost != null && (
        <span className="mt-1.5 ml-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ color: "#166534", background: "rgba(34,197,94,0.16)" }} data-testid={`change-actual-cost-${card.id}`}>
          <Coins className="h-2.5 w-2.5" /> {card.actual_cost} ACU
        </span>
      )}
      {card.fail_notes && (
        <p className="mt-1 text-[11px] leading-snug px-1.5 py-1 rounded" style={{ color: "#b42318", background: "rgba(180,35,24,0.08)" }}>
          Fail: {card.fail_notes}
        </p>
      )}
      {card.rejection && (
        <div className="mt-1 text-[10px] leading-snug px-1.5 py-1 rounded" style={{ color: "#b42318", background: "rgba(180,35,24,0.06)" }} data-testid={`change-rejection-${card.id}`}>
          {card.rejection.reason && <p><span className="font-bold">Reason:</span> {card.rejection.reason}</p>}
          <p style={{ color: MUTE }}>
            {card.rejection.by || "—"}
            {card.rejection.at ? ` · ${new Date(card.rejection.at).toLocaleDateString()}` : ""}
          </p>
        </div>
      )}
      {card.attachments?.length > 0 && (
        <p className="mt-1.5 inline-flex items-center gap-1 text-[10px]" style={{ color: MUTE }}>
          <Paperclip className="h-3 w-3" /> {card.attachments.length}
        </p>
      )}
      {onRequestBuild && (
        <button
          onClick={onRequestBuild}
          data-testid={`change-request-build-${card.id}`}
          className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded border transition-colors"
          style={requested
            ? { color: "#fff", background: "#3f9142", borderColor: "#3f9142" }
            : { color: MUTE, background: "transparent", borderColor: LINE }}
          title={requested
            ? `${isAnalysis ? "Analysis" : "Build"} requested — click to clear`
            : `Flag this CR for the agent to ${isAnalysis ? "analyze" : "build"}`}
        >
          {requested
            ? <><Check className="h-3 w-3" /> {isAnalysis ? "Analysis Requested" : "Build Requested"}</>
            : isAnalysis
              ? <><Search className="h-3 w-3" /> Request Analysis</>
              : <><Hammer className="h-3 w-3" /> Request Build</>}
        </button>
      )}
    </div>
  );
}

function CardMenu({ items, cardId }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <div className="relative shrink-0">
      <button onClick={() => setOpen((o) => !o)} data-testid={`change-menu-${cardId}`}
        className="p-0.5 rounded hover:bg-black/10 transition-colors" style={{ color: MUTE }} aria-label="Card actions">
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-6 z-20 min-w-[130px] rounded-md border bg-white shadow-lg py-1" style={{ borderColor: LINE }}>
            {items.map((it) => (
              <button key={it.testid} data-testid={it.testid}
                onClick={() => { setOpen(false); it.onClick(); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold text-left hover:bg-black/5 transition-colors"
                style={{ color: it.tone === "danger" ? "#b42318" : CHARCOAL }}>
                {it.icon} {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AnalysisInfo() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="analysis-info-button"
        aria-label="Analysis best practices"
        aria-expanded={open}
        className="p-0.5 rounded hover:bg-black/10 transition-colors"
        style={{ color: MUTE }}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 top-6 z-30 w-56 rounded-md border bg-white shadow-lg p-3"
            style={{ borderColor: LINE }}
            data-testid="analysis-info-tooltip"
          >
            <p className="text-[10px] font-bold uppercase tracking-wide mb-1.5" style={{ color: CHARCOAL }}>Analysis · Best Practices</p>
            <ul className="space-y-0.5">
              {ANALYSIS_BEST_PRACTICES.map((p) => (
                <li key={p} className="flex items-start gap-1.5 text-[11px] leading-snug" style={{ color: MUTE }}>
                  <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ background: CHARCOAL }} />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

const ORIGINS = ["Owner", "Tester", "Agent"];
const CARD_TYPES = ["Bug", "Enhancement", "Compliance", "Technical Debt"];
const PRIORITIES = ["P1", "P2", "P3"];
const AREAS = ["UX", "System", "Admin", "Document", "Other"];

const UNSET = "Unassigned";
const ORIGIN_COLORS = { Owner: "#2A2A28", Tester: "#C6A15B", Agent: "#4C8BF5", [UNSET]: "rgba(42,42,40,0.22)" };
const PRIORITY_COLORS = { P1: "#D64545", P2: "#E07B39", P3: "#3AA76D", [UNSET]: "rgba(42,42,40,0.22)" };
const TYPE_COLORS = { Bug: "#D64545", Enhancement: "#4C8BF5", Compliance: "#C6A15B", "Technical Debt": "#7C5CBF", [UNSET]: "rgba(42,42,40,0.22)" };
const AREA_COLORS = { UX: "#4C8BF5", System: "#3AA76D", Admin: "#C6A15B", Document: "#7C5CBF", Other: "#8A8A86", [UNSET]: "rgba(42,42,40,0.22)" };
const PALETTE = ["#4C8BF5", "#3AA76D", "#C6A15B", "#7C5CBF", "#D64545", "#E07B39", "#2A9D8F", "#B5179E", "#457B9D", "#8A8A86"];
const colorFor = (key, map, idx) => map[key] || PALETTE[idx % PALETTE.length];
const STAGE_COLORS = { new: "#9CA3AF", analysis: "#4C8BF5", triage: "#C6A15B", build: "#E07B39", uat: "#3AA76D" };
const STAGE_LABELS = { new: "New", analysis: "Analysis", triage: "Triage", build: "Build/Test", uat: "UAT" };

const WISHLIST_STAGES = ["new", "analysis", "triage"];
const BUILD_STAGES = ["build"];
const DEVQUEUE_STAGES = ["build", "uat"];
const DOMAINCOST_STAGES = ["new", "analysis", "triage", "build"];

function DistDonut({ title, dim, data, total, activeKey, onSlice, testid }) {
  const rows = data.filter((d) => d.count > 0);
  return (
    <div className="rounded-lg border bg-white/60 p-2.5 flex flex-col" style={{ borderColor: LINE }} data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: CHARCOAL }}>{title}</p>
      {total === 0 ? (
        <p className="text-[10px] py-5 text-center" style={{ color: MUTE }}>No active cards</p>
      ) : (
        <div className="flex items-center gap-2.5">
          <div className="relative shrink-0" style={{ width: 62, height: 62 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows} dataKey="count" nameKey="key" cx="50%" cy="50%" innerRadius={19} outerRadius={30} paddingAngle={2} stroke="none" onClick={(d) => d && onSlice(d.key)}>
                  {rows.map((r) => (
                    <Cell key={r.key} fill={r.color} opacity={activeKey && activeKey !== r.key ? 0.3 : 1} cursor="pointer" />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 min-w-0 space-y-0.5">
            {rows.map((r) => {
              const active = activeKey === r.key;
              return (
                <button key={r.key} type="button" onClick={() => onSlice(r.key)}
                  data-testid={`metrics-legend-${dim}-${r.key}`}
                  className={`w-full flex items-center gap-1.5 rounded px-1 py-0.5 -mx-1 text-left transition-colors ${active ? "bg-black/[0.06]" : "hover:bg-black/5"}`}>
                  <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: r.color }} />
                  <span className="text-[10px] truncate flex-1" style={{ color: CHARCOAL, fontWeight: active ? 700 : 400 }}>{r.key}</span>
                  <span className="text-[10px] font-bold" style={{ color: CHARCOAL }}>{r.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function EcuDonut({ title, cards, groups, activeKey, onSelect, testid }) {
  const groupOf = (c) => groups.find((g) => g.match(c));
  const slices = cards.filter((c) => (c.planned_cost || 0) > 0).map((c) => {
    const g = groupOf(c);
    return { id: c.id, value: c.planned_cost, gkey: g?.key, color: g?.color || "#9CA3AF" };
  });
  const legend = groups
    .map((g) => ({ ...g, count: cards.filter(g.match).length, ecu: cards.filter(g.match).reduce((s, c) => s + (c.planned_cost || 0), 0) }))
    .filter((x) => x.count > 0);
  const totalEcu = cards.reduce((s, c) => s + (c.planned_cost || 0), 0);
  return (
    <div className="rounded-lg border bg-white/60 p-2.5 flex flex-col" style={{ borderColor: LINE }} data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: CHARCOAL }}>{title}</p>
      <div className="flex items-center gap-2.5">
        <div className="relative shrink-0" style={{ width: 62, height: 62 }}>
          {slices.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={slices} dataKey="value" nameKey="id" cx="50%" cy="50%" innerRadius={19} outerRadius={30} paddingAngle={1} stroke="none" onClick={(d) => d && onSelect(d.gkey)}>
                  {slices.map((s) => (
                    <Cell key={s.id} fill={s.color} opacity={activeKey && activeKey !== s.gkey ? 0.3 : 1} cursor="pointer" />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full w-full rounded-full border-[6px]" style={{ borderColor: "rgba(42,42,40,0.1)" }} />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-0.5">
          {legend.map((l) => {
            const active = activeKey === l.key;
            return (
              <button key={l.key} type="button" onClick={() => onSelect(l.key)}
                data-testid={`metrics-ecu-legend-${l.key}`}
                className={`w-full flex items-center gap-1.5 rounded px-1 py-0.5 -mx-1 text-left transition-colors ${active ? "bg-black/[0.06]" : "hover:bg-black/5"}`}>
                <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: l.color }} />
                <span className="text-[10px] truncate flex-1" style={{ color: CHARCOAL, fontWeight: active ? 700 : 400 }}>{l.label}</span>
                <span className="text-[10px]" style={{ color: MUTE }}>{l.count} card{l.count === 1 ? "" : "s"}</span>
                <span className="text-[10px] font-bold w-8 text-right" style={{ color: CHARCOAL }}>{l.ecu}</span>
              </button>
            );
          })}
          {legend.length === 0 && <p className="text-[10px]" style={{ color: MUTE }}>No cards</p>}
        </div>
      </div>
      <div className="mt-1.5 pt-1.5 flex items-center justify-between" style={{ borderTop: `1px solid ${LINE}` }} data-testid={`${testid}-total`}>
        <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: MUTE }}>Total planned spend</span>
        <span className="text-[11px] font-bold tabular-nums" style={{ color: CHARCOAL }}>{totalEcu} ECU</span>
      </div>
    </div>
  );
}

const GREEN = "#2E8B57";
const NONGREEN = "#E07B39";

function DevQueue({ cards, activeKey, onSelect }) {
  const green = cards.filter((c) => c.build_requested);
  const non = cards.filter((c) => !c.build_requested);
  const sum = (arr) => arr.reduce((s, c) => s + (c.planned_cost || 0), 0);
  const greenEcu = sum(green), nonEcu = sum(non), total = greenEcu + nonEcu;
  const unbudgeted = green.filter((c) => !(c.planned_cost > 0)).length;
  const op = (k) => (activeKey && activeKey !== k ? 0.4 : 1);

  const Row = ({ k, color, label, count, ecu, warn }) => (
    <tr onClick={() => onSelect(k)} data-testid={`metrics-devqueue-${k}`}
      className="cursor-pointer transition-opacity hover:bg-black/[0.03]" style={{ opacity: op(k) }}>
      <td className="py-1 pr-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: color }} />
          <span className="text-[11px]" style={{ color: CHARCOAL, fontWeight: activeKey === k ? 700 : 400 }}>{label}</span>
          {warn}
        </span>
      </td>
      <td className="py-1 px-2 text-right text-[11px] tabular-nums" style={{ color: CHARCOAL, fontWeight: activeKey === k ? 700 : 400 }}>{count}</td>
      <td className="py-1 pl-2 text-right text-[11px] tabular-nums" style={{ color: CHARCOAL, fontWeight: activeKey === k ? 700 : 400 }}>{ecu}</td>
    </tr>
  );

  return (
    <div className="rounded-lg border bg-white/60 p-2.5 flex flex-col" style={{ borderColor: LINE }} data-testid="metrics-devqueue">
      <div className="flex items-center gap-1.5 mb-2">
        <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: CHARCOAL }}>Dev Queue · Build/UAT</p>
      </div>

      <table className="w-full border-collapse" data-testid="metrics-devqueue-table">
        <thead>
          <tr style={{ borderBottom: `1px solid ${LINE}` }}>
            <th className="py-1 pr-2 text-left text-[9px] font-bold uppercase tracking-wider" style={{ color: MUTE }}>Status</th>
            <th className="py-1 px-2 text-right text-[9px] font-bold uppercase tracking-wider" style={{ color: MUTE }}>Cards</th>
            <th className="py-1 pl-2 text-right text-[9px] font-bold uppercase tracking-wider" style={{ color: MUTE }}>ECU</th>
          </tr>
        </thead>
        <tbody>
          <Row k="greenlit" color={GREEN} label="Greenlit" count={green.length} ecu={greenEcu}
            warn={unbudgeted > 0 ? (
              <button type="button" onClick={(e) => { e.stopPropagation(); onSelect("greenlit_unbudgeted"); }}
                title={`${unbudgeted} greenlit card${unbudgeted === 1 ? "" : "s"} not budgeted (no ECU set) — click to view`}
                data-testid="devqueue-unbudgeted-warning" className="inline-flex shrink-0 transition-opacity hover:opacity-70">
                <AlertTriangle className="h-3.5 w-3.5" style={{ color: "#D64545" }} />
              </button>
            ) : null} />
          <Row k="buildopen" color={NONGREEN} label="Non-greenlit" count={non.length} ecu={nonEcu} />
          <tr style={{ borderTop: `1px solid ${LINE}` }} data-testid="metrics-devqueue-total">
            <td className="py-1 pr-2 text-[11px] font-bold" style={{ color: CHARCOAL }}>Total</td>
            <td className="py-1 px-2 text-right text-[11px] font-bold tabular-nums" style={{ color: CHARCOAL }}>{green.length + non.length}</td>
            <td className="py-1 pl-2 text-right text-[11px] font-bold tabular-nums" style={{ color: CHARCOAL }}>{total}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const buildDist = (cards, dim, options, colors) => {
  const rows = options.map((key, i) => ({ key, color: colorFor(key, colors, i), count: cards.filter((c) => (c[dim] || UNSET) === key).length }));
  const unsetCount = cards.filter((c) => !c[dim]).length;
  if (unsetCount > 0) rows.push({ key: UNSET, color: colors[UNSET] || "rgba(42,42,40,0.22)", count: unsetCount });
  return rows;
};

const dimForKind = (kind) => (kind === "origin" ? "originated_by" : kind === "priority" ? "priority" : kind === "type" ? "type" : kind === "area" ? "area" : null);

function metricMatches(board, filter) {
  if (!filter) return [];
  if (filter.kind === "stage") {
    return board.filter((c) => c.stage === filter.key);
  }
  if (filter.kind === "greenlit") {
    return board.filter((c) => DEVQUEUE_STAGES.includes(c.stage) && c.build_requested);
  }
  if (filter.kind === "greenlit_unbudgeted") {
    return board.filter((c) => DEVQUEUE_STAGES.includes(c.stage) && c.build_requested && !(c.planned_cost > 0));
  }
  if (filter.kind === "buildopen") {
    return board.filter((c) => DEVQUEUE_STAGES.includes(c.stage) && !c.build_requested);
  }
  if (filter.kind === "future") {
    return board.filter((c) => c.archive_status === "future");
  }
  if (filter.kind === "domaincost") {
    const inKanban = (c) => DOMAINCOST_STAGES.includes(c.stage);
    const inBacklog = (c) => c.archive_status === "future";
    const scopeFn = filter.dmode === "kanban" ? inKanban : filter.dmode === "backlog" ? inBacklog : (c) => inKanban(c) || inBacklog(c);
    return board.filter((c) => scopeFn(c) && (c.area || UNSET) === filter.key);
  }
  const dim = dimForKind(filter.kind);
  return board.filter((c) => c.stage !== "closed").filter((c) => (c[dim] || UNSET) === filter.key);
}

function DomainBreakout({ kanbanCards, backlogCards, activeKey, onSelect }) {
  const [mode, setMode] = useState("kanban"); // both | kanban | backlog
  const [topOnly, setTopOnly] = useState(true);
  const cards = mode === "kanban" ? kanbanCards : mode === "backlog" ? backlogCards : [...kanbanCards, ...backlogCards];
  const totalEcu = cards.reduce((s, c) => s + (c.planned_cost || 0), 0);
  const keys = Array.from(new Set(cards.map((c) => c.area || UNSET)));
  const rows = keys
    .map((k) => {
      const cs = cards.filter((c) => (c.area || UNSET) === k);
      return { key: k, label: k === UNSET ? "Unassigned" : k, count: cs.length, ecu: cs.reduce((s, c) => s + (c.planned_cost || 0), 0), color: AREA_COLORS[k] || AREA_COLORS[UNSET] };
    })
    .sort((a, b) => b.ecu - a.ecu);
  const shownRows = topOnly ? rows.slice(0, 5) : rows;
  const otherRows = topOnly ? rows.slice(5) : [];
  const otherEcu = otherRows.reduce((s, r) => s + r.ecu, 0);
  const op = (k) => (!activeKey || activeKey === k ? 1 : 0.4);
  const pct = (ecu) => (totalEcu > 0 ? Math.round((ecu / totalEcu) * 100) : 0);
  const switchMode = (m) => { setMode(m); onSelect(null, m); };
  const ModeTab = ({ m, label }) => (
    <button type="button" onClick={() => switchMode(m)} data-testid={`domaincost-mode-${m}`}
      className="text-[10px] font-semibold px-2 py-0.5 rounded transition-colors"
      style={mode === m ? { background: CHARCOAL, color: "#F5F2EA" } : { color: MUTE }}>{label}</button>
  );
  const CountTab = ({ v, label }) => (
    <button type="button" onClick={() => setTopOnly(v)} data-testid={`domaincost-count-${v ? "top5" : "all"}`}
      className="text-[10px] font-semibold px-2 py-0.5 rounded transition-colors"
      style={topOnly === v ? { background: CHARCOAL, color: "#F5F2EA" } : { color: MUTE }}>{label}</button>
  );
  return (
    <div className="rounded-lg border bg-white/60 p-2.5 flex flex-col" style={{ borderColor: LINE }} data-testid="metrics-domaincost">
      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: CHARCOAL }}>Domain Breakout</p>
      <div className="flex items-center flex-wrap gap-1.5 my-1.5">
        <div className="flex items-center gap-0.5 rounded-md border p-0.5" style={{ borderColor: LINE, background: "rgba(255,255,255,0.5)" }} data-testid="domaincost-mode-toggle">
          <ModeTab m="kanban" label="Kanban" />
          <ModeTab m="backlog" label="Backlog" />
          <ModeTab m="both" label="Both" />
        </div>
        <div className="flex items-center gap-0.5 rounded-md border p-0.5" style={{ borderColor: LINE, background: "rgba(255,255,255,0.5)" }} data-testid="domaincost-count-toggle">
          <CountTab v={true} label="Top 5" />
          <CountTab v={false} label="All" />
        </div>
      </div>
      <table className="w-full border-collapse" data-testid="metrics-domaincost-table">
        <thead>
          <tr style={{ borderBottom: `1px solid ${LINE}` }}>
            <th className="py-1 pr-2 text-left text-[9px] font-bold uppercase tracking-wider" style={{ color: MUTE }}>Domain</th>
            <th className="py-1 px-2 text-right text-[9px] font-bold uppercase tracking-wider" style={{ color: MUTE }}>ECU</th>
            <th className="py-1 pl-2 text-right text-[9px] font-bold uppercase tracking-wider" style={{ color: MUTE }}>% of total</th>
          </tr>
        </thead>
        <tbody>
          {shownRows.map((r) => (
            <tr key={r.key} onClick={() => onSelect(r.key, mode)} data-testid={`metrics-domaincost-${r.key}`}
              className="cursor-pointer transition-opacity hover:bg-black/[0.03]" style={{ opacity: op(r.key) }}>
              <td className="py-1 pr-2">
                <span className="inline-flex items-center gap-1.5 max-w-full">
                  <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: r.color }} />
                  <span className="text-[11px] truncate" style={{ color: CHARCOAL, fontWeight: activeKey === r.key ? 700 : 400 }}>{r.label}</span>
                </span>
              </td>
              <td className="py-1 px-2 text-right text-[11px] tabular-nums" style={{ color: CHARCOAL, fontWeight: activeKey === r.key ? 700 : 400 }}>{r.ecu}</td>
              <td className="py-1 pl-2 text-right text-[11px] tabular-nums" style={{ color: CHARCOAL, fontWeight: activeKey === r.key ? 700 : 400 }}>{pct(r.ecu)}%</td>
            </tr>
          ))}
          {shownRows.length === 0 && <tr><td colSpan={3} className="py-2 text-[10px]" style={{ color: MUTE }}>No cards in these lanes.</td></tr>}
          {otherRows.length > 0 && (
            <tr data-testid="metrics-domaincost-other" style={{ color: MUTE }}>
              <td className="py-1 pr-2">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: "rgba(42,42,40,0.25)" }} />
                  <span className="text-[11px] italic">Other ({otherRows.length} domain{otherRows.length === 1 ? "" : "s"})</span>
                </span>
              </td>
              <td className="py-1 px-2 text-right text-[11px] tabular-nums">{otherEcu}</td>
              <td className="py-1 pl-2 text-right text-[11px] tabular-nums">{pct(otherEcu)}%</td>
            </tr>
          )}
          <tr style={{ borderTop: `1px solid ${LINE}` }} data-testid="metrics-domaincost-total">
            <td className="py-1 pr-2 text-[11px] font-bold" style={{ color: CHARCOAL }}>Total</td>
            <td className="py-1 px-2 text-right text-[11px] font-bold tabular-nums" style={{ color: CHARCOAL }}>{totalEcu}</td>
            <td className="py-1 pl-2 text-right text-[11px] font-bold tabular-nums" style={{ color: CHARCOAL }}>{rows.length ? "100%" : "0%"}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ExecSummaryWindow({ onClose }) {
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState({ generated_at: null, updated_at: null });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState("leadership"); // leadership | brief
  const [pos, setPos] = useState({ x: Math.max(24, window.innerWidth / 2 - 300), y: 96 });
  const drag = useRef(null);
  const cmLink = `${window.location.origin}/admin`;

  useEffect(() => {
    adminService.getExecSummary()
      .then((d) => { setContent(d.content || ""); setMeta({ generated_at: d.generated_at, updated_at: d.updated_at }); if (d.mode) setMode(d.mode); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!drag.current) return;
      const nx = Math.min(Math.max(0, e.clientX - drag.current.dx), window.innerWidth - 120);
      const ny = Math.min(Math.max(0, e.clientY - drag.current.dy), window.innerHeight - 40);
      setPos({ x: nx, y: ny });
    };
    const up = () => { drag.current = null; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, []);

  const startDrag = (e) => { drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y }; };

  const regenerate = async (m) => {
    const useMode = m || mode;
    setMode(useMode);
    setGenerating(true);
    try {
      const d = await adminService.generateExecSummary(useMode);
      setContent(d.content || ""); setMeta({ generated_at: d.generated_at, updated_at: d.updated_at });
      setEditing(false);
      toast.success(`Regenerated (${useMode === "brief" ? "Board Brief" : "Leadership"})`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };
  const switchTone = (m) => { if (m !== mode && !generating) regenerate(m); };

  const startEdit = () => { setDraft(content); setEditing(true); };
  const saveEdit = async () => {
    try {
      const d = await adminService.saveExecSummary(draft);
      setContent(d.content || ""); setMeta({ generated_at: d.generated_at, updated_at: d.updated_at });
      setEditing(false);
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyAll = async () => {
    const text = `Change Management — Executive Summary\n\n${content}\n\nView the Change Management board: ${cmLink}`;
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  return (
    <div className="fixed z-[60] rounded-xl border shadow-2xl bg-[#F5F2EA] flex flex-col"
      style={{ left: pos.x, top: pos.y, width: 600, maxWidth: "92vw", maxHeight: "80vh", borderColor: LINE }}
      data-testid="exec-summary-window">
      <div onMouseDown={startDrag} className="flex items-center gap-2 px-3 py-2 rounded-t-xl cursor-move select-none" style={{ background: CHARCOAL }} data-testid="exec-summary-drag">
        <GripHorizontal className="h-3.5 w-3.5" style={{ color: "rgba(245,242,234,0.6)" }} />
        <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#F5F2EA" }}>Executive Summary · Change Management</span>
        <button onClick={onClose} className="ml-auto" data-testid="exec-summary-close"><X className="h-4 w-4" style={{ color: "rgba(245,242,234,0.8)" }} /></button>
      </div>
      <div className="flex items-center gap-1.5 px-3 py-2 border-b flex-wrap" style={{ borderColor: LINE }}>
        <button onClick={() => regenerate()} disabled={generating} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md disabled:opacity-50" style={{ background: CHARCOAL, color: "#F5F2EA" }} data-testid="exec-summary-regenerate">
          {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />} Regenerate
        </button>
        <div className="flex items-center gap-0.5 rounded-md border p-0.5" style={{ borderColor: LINE, background: "rgba(255,255,255,0.5)" }} data-testid="exec-summary-tone-toggle">
          <button type="button" onClick={() => switchTone("brief")} disabled={generating} data-testid="exec-summary-tone-brief"
            className="text-[10px] font-semibold px-2 py-0.5 rounded transition-colors disabled:opacity-50"
            style={mode === "brief" ? { background: CHARCOAL, color: "#F5F2EA" } : { color: MUTE }}>Board Brief</button>
          <button type="button" onClick={() => switchTone("leadership")} disabled={generating} data-testid="exec-summary-tone-leadership"
            className="text-[10px] font-semibold px-2 py-0.5 rounded transition-colors disabled:opacity-50"
            style={mode === "leadership" ? { background: CHARCOAL, color: "#F5F2EA" } : { color: MUTE }}>Leadership</button>
        </div>
        {!editing ? (
          <button onClick={startEdit} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border" style={{ borderColor: LINE, color: CHARCOAL }} data-testid="exec-summary-edit"><Pencil className="h-3 w-3" /> Edit</button>
        ) : (
          <button onClick={saveEdit} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border" style={{ borderColor: LINE, color: CHARCOAL }} data-testid="exec-summary-save"><Save className="h-3 w-3" /> Save</button>
        )}
        <button onClick={copyAll} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border" style={{ borderColor: LINE, color: CHARCOAL }} data-testid="exec-summary-copy"><Copy className="h-3 w-3" /> Copy</button>
        <a href={cmLink} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border ml-auto" style={{ borderColor: LINE, color: CHARCOAL }} data-testid="exec-summary-cm-link"><ExternalLink className="h-3 w-3" /> Change Management</a>
      </div>
      <div className="p-3 overflow-y-auto" style={{ flex: 1 }}>
        {loading ? (
          <div className="flex items-center gap-2 text-[12px] py-6 justify-center" style={{ color: MUTE }}><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : editing ? (
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full h-[320px] text-[12px] leading-relaxed p-2 rounded-md border bg-white/70 resize-none" style={{ borderColor: LINE, color: CHARCOAL }} data-testid="exec-summary-textarea" />
        ) : content ? (
          <div className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: CHARCOAL }} data-testid="exec-summary-content">{content}</div>
        ) : (
          <div className="text-center py-8 text-[12px]" style={{ color: MUTE }} data-testid="exec-summary-empty">
            No summary yet. Click <span className="font-semibold">Regenerate</span> to generate one from the live board data.
          </div>
        )}
      </div>
      {!editing && (meta.updated_at || meta.generated_at) && (
        <div className="px-3 py-1.5 border-t text-[10px]" style={{ borderColor: LINE, color: MUTE }} data-testid="exec-summary-meta">
          {meta.generated_at ? `Generated ${new Date(meta.generated_at).toLocaleString()}` : ""}
          {meta.updated_at && meta.updated_at !== meta.generated_at ? ` · Edited ${new Date(meta.updated_at).toLocaleString()}` : ""}
        </div>
      )}
    </div>
  );
}


export function ChangeMetricsPanel({ filter, onDrill, refreshToken }) {
  const [board, setBoard] = useState([]);
  const [areas, setAreas] = useState(AREAS);
  const [origins, setOrigins] = useState(ORIGINS);
  const [archive, setArchive] = useState([]);
  const [view, setView] = useState("kanban"); // kanban | backlog
  const [execOpen, setExecOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const d = await adminService.listChangeCards();
      setBoard(d.board || []);
      setArchive(d.archive || []);
      if (d.picklists?.areas) setAreas(d.picklists.areas);
      if (d.picklists?.origins) setOrigins(d.picklists.origins);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshToken]);

  if (loading) {
    return <div className="h-full min-h-[120px] flex items-center justify-center" style={{ color: MUTE }} data-testid="change-metrics-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  const isBacklog = view === "backlog";
  // Backlog view = Future/Backlog items only; Kanban view = active (non-closed) board cards.
  const futureCards = archive.filter((c) => c.archive_status === "future");
  const scope = isBacklog ? futureCards : board;
  const active = scope.filter((c) => c.stage !== "closed");
  const totalActive = active.length;
  const originDist = buildDist(active, "originated_by", origins, ORIGIN_COLORS);
  const priorityDist = buildDist(active, "priority", PRIORITIES, PRIORITY_COLORS);
  const typeDist = buildDist(active, "type", CARD_TYPES, TYPE_COLORS);
  const areaDist = buildDist(active, "area", areas, AREA_COLORS);
  // Second row (Wishlist + Dev Queue) always reflects the active Kanban board,
  // independent of the Kanban/Backlog toggle (toggle affects the top charts only).
  const wishlistCards = [...board.filter((c) => WISHLIST_STAGES.includes(c.stage)), ...futureCards];
  const devQueueCards = board.filter((c) => DEVQUEUE_STAGES.includes(c.stage));

  // Always select the clicked metric (never toggle to null here) so the Zone C
  // drill-down always swaps to the new metric instead of falling back to the board.
  // Clearing happens only via the "Back to board" control in Zone C.
  // scope is carried on the filter so the Zone C drilldown uses the same card set.
  const drill = (kind, key, label) => onDrill({ kind, key, label, scope: view });
  // Second-row drill-downs always target the active board, matching their charts.
  const drillBoard = (kind, key, label) => onDrill({ kind, key, label, scope: "kanban" });
  const activeKeyFor = (kind) => (filter && filter.kind === kind ? filter.key : null);

  // Wishlist donut group descriptors (slice color + legend + drill target per group).
  const wishlistGroups = [
    ...WISHLIST_STAGES.map((st) => ({ key: st, label: STAGE_LABELS[st], color: STAGE_COLORS[st], match: (c) => c.stage === st && c.archive_status !== "future", dkind: "stage", dkey: st })),
    { key: "future", label: "Future/Backlog", color: "#7C6FD6", match: (c) => c.archive_status === "future", dkind: "future", dkey: "future", scope: "backlog" },
  ];
  const groupActiveKey = (groups) => (groups.find((g) => filter && filter.kind === g.dkind && filter.key === g.dkey)?.key ?? null);
  const selectGroup = (groups) => (key) => { const g = groups.find((x) => x.key === key); if (g) onDrill({ kind: g.dkind, key: g.dkey, label: g.label, scope: g.scope || "kanban" }); };

  // Dev Queue bars (Build/UAT lane): greenlit vs non-greenlit, drill per segment.
  const dqActiveKey = filter?.kind === "greenlit" ? "greenlit" : filter?.kind === "buildopen" ? "buildopen" : null;
  const dqSelect = (key) => {
    if (key === "greenlit") return drillBoard("greenlit", "greenlit", "Green Lights (committed) · Build/UAT");
    if (key === "greenlit_unbudgeted") return drillBoard("greenlit_unbudgeted", "greenlit_unbudgeted", "Greenlit · no budget set · Build/UAT");
    return drillBoard("buildopen", "buildopen", "Non-greenlit · Build/UAT");
  };
  // Domain Cost Breakout (Analysis + Triage + Build/Test lanes): ECU by domain, drill per row.
  const dcActiveKey = filter?.kind === "domaincost" ? filter.key : null;
  const dcSelect = (key, mode) => {
    if (!key) return onDrill(null);
    onDrill({ kind: "domaincost", key, dmode: mode, label: `Domain · ${key === UNSET ? "Unassigned" : key}`, scope: "kanban" });
  };

  const switchView = (v) => { setView(v); onDrill(null); };
  const ViewTab = ({ v, label, badge }) => (
    <button type="button" onClick={() => switchView(v)} data-testid={`metrics-view-${v}`}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-md transition-colors"
      style={view === v ? { background: CHARCOAL, color: "#F5F2EA" } : { color: MUTE }}>
      {label}
      {badge != null && badge > 0 && (
        <span data-testid={`metrics-view-${v}-badge`}
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none"
          style={view === v ? { background: "#F5F2EA", color: CHARCOAL } : { background: "rgba(42,42,40,0.12)", color: CHARCOAL }}>
          {badge}
        </span>
      )}
    </button>
  );

  return (
    <div className="flex flex-col gap-2" data-testid="change-metrics-panel">
      {execOpen && <ExecSummaryWindow onClose={() => setExecOpen(false)} />}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 rounded-lg border p-1" style={{ borderColor: LINE, background: "rgba(255,255,255,0.5)" }} data-testid="metrics-view-toggle">
          <ViewTab v="kanban" label="Kanban View" />
          <ViewTab v="backlog" label="Backlog View" badge={futureCards.length} />
        </div>
        <button onClick={() => setExecOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border ml-auto transition-colors hover:bg-black/[0.03]" style={{ borderColor: LINE, color: CHARCOAL }} data-testid="exec-summary-open">
          <FileText className="h-3.5 w-3.5" /> Executive Summary
        </button>
      </div>
      {/* Row 1 — distribution charts (eye candy, first) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <DistDonut title={`By Originated By · ${totalActive} ${isBacklog ? "backlog" : "active"}`} dim="origin" testid="metrics-donut-origin" data={originDist} total={totalActive}
          activeKey={activeKeyFor("origin")} onSlice={(k) => drill("origin", k, `Originated By · ${k}`)} />
        <DistDonut title="By Priority" dim="priority" testid="metrics-donut-priority" data={priorityDist} total={totalActive}
          activeKey={activeKeyFor("priority")} onSlice={(k) => drill("priority", k, `Priority · ${k}`)} />
        <DistDonut title="By Type" dim="type" testid="metrics-donut-type" data={typeDist} total={totalActive}
          activeKey={activeKeyFor("type")} onSlice={(k) => drill("type", k, `Type · ${k}`)} />
        <DistDonut title="By Domain" dim="area" testid="metrics-donut-area" data={areaDist} total={totalActive}
          activeKey={activeKeyFor("area")} onSlice={(k) => drill("area", k, `Domain · ${k}`)} />
      </div>
      {/* Row 2 — Wishlist donut · Domain Cost Breakout · Dev Queue */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <EcuDonut title="Wishlist" cards={wishlistCards} groups={wishlistGroups}
          activeKey={groupActiveKey(wishlistGroups)} onSelect={selectGroup(wishlistGroups)} testid="metrics-ecu-wishlist" />
        <DomainBreakout kanbanCards={board.filter((c) => DOMAINCOST_STAGES.includes(c.stage))} backlogCards={futureCards} activeKey={dcActiveKey} onSelect={dcSelect} />
        <DevQueue cards={devQueueCards} activeKey={dqActiveKey} onSelect={dqSelect} />
      </div>
    </div>
  );
}

// Renders the underlying cards for a clicked metric in the Zone C workspace.
// Loads only `d.board` (the same source the metric counts use) so the list always
// matches the clicked metric exactly.
export function ChangeMetricsDrilldown({ filter, onClose, onChange, refreshToken }) {
  const [board, setBoard] = useState([]);
  const [archive, setArchive] = useState([]);
  const [picklists, setPicklists] = useState({ origins: ORIGINS, areas: AREAS, card_types: CARD_TYPES });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const load = useCallback(async () => {
    try {
      const d = await adminService.listChangeCards();
      setBoard(d.board || []);
      setArchive(d.archive || []);
      if (d.picklists) setPicklists(d.picklists);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshToken]);

  // Match the metric's card source: Backlog view = Future items, Kanban view = board.
  // Domain Breakout spans New+Analysis+Triage+Build/Test + Backlog, so use board + future.
  const isBacklog = filter?.scope === "backlog";
  const source = filter?.kind === "domaincost"
    ? [...board, ...archive.filter((c) => c.archive_status === "future")]
    : (isBacklog ? archive.filter((c) => c.archive_status === "future") : board);
  const matches = metricMatches(source, filter);
  const editingCard = editingId ? source.find((c) => c.id === editingId) : null;

  // Promote a Future/Backlog card into the active pipeline (New lane).
  // Setting stage clears archive_status server-side, so it lands on the board.
  const promote = async (id) => {
    try {
      await adminService.updateChangeCard(id, { stage: "new" });
      toast.success("Sent to New — now in the active pipeline");
      await load();
      onChange?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) {
    return <div className="h-full flex items-center justify-center" style={{ color: MUTE }}><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  if (editingCard) {
    return <CardEditor card={editingCard} onClose={() => setEditingId(null)} onChanged={() => { load(); onChange?.(); }} areas={picklists.areas} cardTypes={picklists.card_types} origins={picklists.origins} />;
  }
  return (
    <div className="h-full flex flex-col" data-testid="change-metrics-drilldown">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: CHARCOAL }} data-testid="metrics-drilldown-title">
          {matches.length} card{matches.length === 1 ? "" : "s"} · {filter?.label}
        </span>
        <button onClick={onClose} data-testid="metrics-drilldown-close"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded border transition-opacity hover:opacity-75"
          style={{ borderColor: LINE, color: CHARCOAL }}>
          <ArrowLeft className="h-3.5 w-3.5" /> Back to board
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
          {matches.map((c) => (
            <Card key={c.id} card={c} menu={
              <CardMenu cardId={c.id} items={[
                { label: "View details", testid: `metrics-view-${c.id}`, icon: <Info className="h-3 w-3" />, onClick: () => setEditingId(c.id) },
                ...(isBacklog ? [{ label: "Send to New", testid: `metrics-send-new-${c.id}`, icon: <ArrowRight className="h-3 w-3" />, onClick: () => promote(c.id) }] : []),
              ]} />
            } />
          ))}
          {matches.length === 0 && <p className="text-[11px] py-4" style={{ color: MUTE }}>No matching cards.</p>}
        </div>
      </div>
    </div>
  );
}

function AttachmentThumb({ att, onOpen, onDownload }) {
  const [url, setUrl] = useState(null);
  const isImg = (att.content_type || "").startsWith("image/");
  useEffect(() => {
    let revoke;
    if (isImg) {
      adminService.downloadChangeAttachment(att.path).then((blob) => {
        const u = URL.createObjectURL(blob); revoke = u; setUrl(u);
      }).catch(() => {});
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [att.path, isImg]);
  return (
    <div className="flex items-center gap-2 text-xs bg-white/70 border rounded px-2.5 py-1.5" style={{ borderColor: LINE }} data-testid={`editor-attachment-${att.id}`}>
      <button onClick={() => onOpen(att)} title="Open" className="h-9 w-9 rounded overflow-hidden flex items-center justify-center shrink-0 cursor-pointer hover:opacity-80 transition-opacity" style={{ background: "rgba(42,42,40,0.06)" }} data-testid={`editor-open-thumb-${att.id}`}>
        {isImg && url ? <img src={url} alt={att.name} className="h-full w-full object-cover" /> : <FileText className="h-4 w-4" style={{ color: MUTE }} />}
      </button>
      <button onClick={() => onOpen(att)} className="truncate flex-1 text-left hover:underline cursor-pointer" style={{ color: CHARCOAL }} title="Open" data-testid={`editor-open-name-${att.id}`}>{att.name}</button>
      <button onClick={() => onOpen(att)} className="inline-flex items-center gap-1 shrink-0" style={{ color: MUTE }} data-testid={`editor-open-${att.id}`}><ExternalLink className="h-3 w-3" /> Open</button>
      <button onClick={() => onDownload(att)} className="inline-flex items-center gap-1 shrink-0" style={{ color: MUTE }} data-testid={`editor-download-${att.id}`}><Download className="h-3 w-3" /> Download</button>
    </div>
  );
}

function saveErrMsg(e) {
  if (!e?.response) return "Couldn't reach the server — your edits are kept here. Retry when your connection is back.";
  const s = e.response.status;
  if (s === 404) return "This card wasn't found on the server (it may have been deleted elsewhere). Your edits are kept here — use Retry to try again.";
  if (s === 401 || s === 403) return "Your session may have expired. Your edits are kept here — use Retry after signing back in.";
  return `The save failed (server error ${s}). Your edits are kept here — use Retry to try again.`;
}

const AUTOSAVE_DELAY = 1500;

function CardEditor({ card, onClose, onChanged, areas = AREAS, cardTypes = CARD_TYPES, origins = ORIGINS }) {
  const [title, setTitle] = useState(card.title || "");
  const [description, setDescription] = useState(card.description || "");
  const [notes, setNotes] = useState(card.notes || "");
  const [analystNotes, setAnalystNotes] = useState(card.analyst_notes || "");
  const [originatedBy, setOriginatedBy] = useState(card.originated_by || "");
  const [cardType, setCardType] = useState(card.type || "");
  const [priority, setPriority] = useState(card.priority || "");
  const [area, setArea] = useState(card.area || "");
  const [pain, setPain] = useState(card.pain || "");
  const [plannedCost, setPlannedCost] = useState(card.planned_cost == null ? "" : String(card.planned_cost));
  const [actualCost, setActualCost] = useState(card.actual_cost == null ? "" : String(card.actual_cost));
  const [attachments, setAttachments] = useState(card.attachments || []);
  const [saving, setSaving] = useState(false);
  const [autosaving, setAutosaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const isNew = card.stage === "new";
  const fmt = (v) => { try { return new Date(v).toLocaleString(); } catch { return "—"; } };

  const buildPayload = useCallback(() => ({
    title: title.trim(), description, notes, originated_by: originatedBy, type: cardType, priority, area, pain,
    analyst_notes: analystNotes,
    planned_cost: plannedCost === "" ? null : Number(plannedCost),
    actual_cost: actualCost === "" ? null : Number(actualCost),
  }), [title, description, notes, originatedBy, cardType, priority, area, pain, analystNotes, plannedCost, actualCost]);

  // Snapshot of what's currently persisted on the server, to detect unsaved edits.
  const lastSavedRef = useRef(JSON.stringify(buildPayload()));
  const inFlightRef = useRef(false);

  const doSave = useCallback(async ({ silent } = {}) => {
    if (inFlightRef.current) return false;
    const payload = buildPayload();
    if (!payload.title) { if (!silent) toast.error("Title is required"); return false; }
    inFlightRef.current = true;
    silent ? setAutosaving(true) : setSaving(true);
    try {
      await adminService.updateChangeCard(card.id, payload);
      lastSavedRef.current = JSON.stringify(payload);
      setSaveError(null);
      setLastSavedAt(new Date());
      if (!silent) toast.success("Saved");
      onChanged();
      return true;
    } catch (e) {
      // Never close or discard edits on failure — surface an inline error with Retry.
      setSaveError(saveErrMsg(e));
      if (!silent) toast.error(apiError(e));
      return false;
    } finally {
      inFlightRef.current = false;
      silent ? setAutosaving(false) : setSaving(false);
    }
  }, [buildPayload, card.id, onChanged]);

  const save = () => doSave({ silent: false });

  // Debounced autosave: persist edits automatically after a short pause.
  useEffect(() => {
    if (!title.trim()) return;
    if (JSON.stringify(buildPayload()) === lastSavedRef.current) return;
    const t = setTimeout(() => { doSave({ silent: true }); }, AUTOSAVE_DELAY);
    return () => clearTimeout(t);
  }, [buildPayload, doSave, title]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await adminService.uploadChangeAttachment(card.id, f);
      setAttachments(r.card.attachments || []);
      toast.success("Attachment added");
      onChanged();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (att) => {
    try {
      const blob = await adminService.downloadChangeAttachment(att.path);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = att.name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openAtt = async (att) => {
    const w = window.open("", "_blank");
    try {
      const blob = await adminService.downloadChangeAttachment(att.path);
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url; else window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); toast.error(apiError(e)); }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto" data-testid="change-card-editor">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onClose} data-testid="editor-close" className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: CHARCOAL }}>
          <ArrowLeft className="h-4 w-4" /> Close · Back to board
        </button>
        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded" style={{ color: MUTE, background: "rgba(42,42,40,0.06)" }}>{card.stage}</span>
      </div>
      <p className="mb-4 text-[10px]" style={{ color: MUTE }} data-testid="editor-dates">Created {fmt(card.created_at)} · Updated {fmt(card.updated_at)}</p>
      <div className="max-w-2xl space-y-4">
        {/* Row 1: Bug ID | Priority | ECU | ACU */}
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Bug ID</label>
            <div className="mt-1 w-full text-sm bg-black/[0.04] border rounded px-3 py-2 font-mono font-semibold" style={{ borderColor: LINE, color: CHARCOAL }} data-testid="editor-bug-id">
              {card.cr_number != null ? `CR-${String(card.cr_number).padStart(3, "0")}` : "—"}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="editor-priority"
              className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none" style={{ borderColor: LINE, color: CHARCOAL }}>
              <option value="">—</option>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>ECU <span className="font-normal normal-case" style={{ color: MUTE }}>(planned)</span></label>
            <input type="number" min="0" step="1" value={plannedCost} onChange={(e) => setPlannedCost(e.target.value)} data-testid="editor-planned-cost"
              placeholder="e.g. 25"
              className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none" style={{ borderColor: LINE, color: CHARCOAL }} />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>ACU <span className="font-normal normal-case" style={{ color: MUTE }}>(actual)</span></label>
            <input type="number" min="0" step="1" value={actualCost} onChange={(e) => setActualCost(e.target.value)} data-testid="editor-actual-cost"
              placeholder="e.g. 22"
              className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none" style={{ borderColor: LINE, color: CHARCOAL }} />
          </div>
        </div>
        {/* Row 2: Title */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="editor-title"
            className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none" style={{ borderColor: LINE, color: CHARCOAL }} />
        </div>
        {/* Row 3: Description */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Description</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="editor-description"
            className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none" style={{ borderColor: LINE, color: CHARCOAL }} />
        </div>
        {/* Row 4: Originated By | Area | Type */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Originated By</label>
            <select value={originatedBy} onChange={(e) => setOriginatedBy(e.target.value)} data-testid="editor-originated-by"
              className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none" style={{ borderColor: LINE, color: CHARCOAL }}>
              <option value="">—</option>
              {(originatedBy && !origins.includes(originatedBy) ? [originatedBy, ...origins] : origins).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Domain</label>
            <select value={area} onChange={(e) => setArea(e.target.value)} data-testid="editor-area"
              className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none" style={{ borderColor: LINE, color: CHARCOAL }}>
              <option value="">—</option>
              {(area && !areas.includes(area) ? [area, ...areas] : areas).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Type</label>
            <select value={cardType} onChange={(e) => setCardType(e.target.value)} data-testid="editor-type"
              className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none" style={{ borderColor: LINE, color: CHARCOAL }}>
              <option value="">—</option>
              {(cardType && !cardTypes.includes(cardType) ? [cardType, ...cardTypes] : cardTypes).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        {/* Row 5: Pain */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Pain</label>
          <textarea value={pain} onChange={(e) => setPain(e.target.value)} data-testid="editor-pain" rows={3}
            placeholder="What pain does this address? (free text)"
            className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none resize-y" style={{ borderColor: LINE, color: CHARCOAL }} />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Detailed Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="editor-notes" rows={10}
            placeholder="Extensive notes, context, decisions…"
            className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none resize-y" style={{ borderColor: LINE, color: CHARCOAL }} />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Analyst Notes</label>
          <textarea value={analystNotes} onChange={(e) => setAnalystNotes(e.target.value)} data-testid="editor-analyst-notes" rows={12}
            placeholder="Analyst findings for the Analysis step — AS-IS/TO-BE, functional design, dependencies, cost, impact, admin functions, compliance, benefits vs risks, risk mitigation…"
            className="mt-1 w-full text-sm bg-white/70 border rounded px-3 py-2 outline-none resize-y" style={{ borderColor: LINE, color: CHARCOAL }} />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: MUTE }}>Attachments</label>
          <div className="mt-1 space-y-1.5">
            {attachments.map((a) => (
              <AttachmentThumb key={a.id} att={a} onOpen={openAtt} onDownload={download} />
            ))}
            {attachments.length === 0 && <p className="text-[11px]" style={{ color: MUTE }}>No attachments yet.</p>}
          </div>
          {isNew ? (
            <label className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border cursor-pointer hover:opacity-75" style={{ borderColor: LINE, color: CHARCOAL }} data-testid="editor-upload-label">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} Add file (image/PDF, ≤10MB)
              <input type="file" accept=".png,.jpg,.jpeg,.gif,.webp,.pdf" className="hidden" onChange={onFile} disabled={uploading} data-testid="editor-upload-input" />
            </label>
          ) : (
            <p className="mt-2 text-[10px]" style={{ color: MUTE }}>Uploads are available only while the card is in the New lane.</p>
          )}
        </div>
        {saveError && (
          <div className="rounded-lg border px-3 py-2.5 flex items-start gap-2" data-testid="editor-save-error"
            style={{ borderColor: "rgba(180,35,24,0.35)", background: "rgba(180,35,24,0.06)" }}>
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#b42318" }} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold" style={{ color: "#b42318" }}>Save failed</p>
              <p className="text-[11px] mt-0.5" style={{ color: CHARCOAL }}>{saveError}</p>
            </div>
            <button onClick={save} disabled={saving} data-testid="editor-save-retry"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border shrink-0 disabled:opacity-50 hover:opacity-75 transition-opacity"
              style={{ borderColor: "rgba(180,35,24,0.35)", color: "#b42318" }}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Retry
            </button>
          </div>
        )}
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} data-testid="editor-save"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 h-10 rounded disabled:opacity-50 hover:opacity-90 transition-opacity"
            style={{ background: CHARCOAL, color: "#fff" }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
          <span className="text-[11px]" style={{ color: saveError ? "#b42318" : MUTE }} data-testid="editor-autosave-status">
            {autosaving ? (
              <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Autosaving…</span>
            ) : saveError ? (
              "Autosave paused — unsaved changes"
            ) : lastSavedAt ? (
              `Autosaved ${lastSavedAt.toLocaleTimeString()}`
            ) : (
              "Autosaves as you type"
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

function MiniBtn({ onClick, children, tone = "default", testid }) {
  const styles = {
    default: { color: CHARCOAL, borderColor: LINE },
    dark: { color: "#fff", background: CHARCOAL, borderColor: CHARCOAL },
    danger: { color: "#b42318", borderColor: "rgba(180,35,24,0.35)" },
  }[tone];
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-1 rounded border transition-opacity hover:opacity-75"
      style={styles}>
      {children}
    </button>
  );
}

function EditableList({ title, items, onChange, testid }) {
  const update = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: MUTE }}>{title}</p>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input value={it} onChange={(e) => update(i, e.target.value)}
              data-testid={`${testid}-item-${i}`}
              className="flex-1 text-sm bg-white/70 border rounded px-2.5 py-1.5 outline-none" style={{ borderColor: LINE, color: CHARCOAL }} />
            <button onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}
              className="shrink-0 p-1.5 rounded border transition-opacity hover:opacity-75" style={{ borderColor: LINE, color: "#D64545" }}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="text-[11px] py-1" style={{ color: MUTE }}>No items yet.</p>}
      </div>
      <button onClick={add} data-testid={`${testid}-add`}
        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border transition-opacity hover:opacity-75"
        style={{ borderColor: LINE, color: CHARCOAL }}>
        <Plus className="h-3 w-3" /> Add item
      </button>
    </div>
  );
}

function PicklistManager({ origins: initOrigins, areas: initAreas, cardTypes: initTypes, onClose, onSaved }) {
  const [origins, setOrigins] = useState(initOrigins || []);
  const [areas, setAreas] = useState(initAreas || []);
  const [types, setTypes] = useState(initTypes || []);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const pl = await adminService.updateChangePicklists({ origins, areas, card_types: types });
      toast.success("Picklists saved");
      onSaved(pl);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={onClose} data-testid="picklist-manager">
      <div className="w-full max-w-2xl rounded-xl border bg-[#F5F3EE] shadow-xl p-5 max-h-[85vh] overflow-y-auto"
        style={{ borderColor: LINE }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-bold" style={{ color: CHARCOAL }}>Manage picklists</h3>
          <button onClick={onClose} data-testid="picklist-close" style={{ color: MUTE }}><X className="h-4 w-4" /></button>
        </div>
        <p className="text-[11px] mb-4" style={{ color: MUTE }}>
          Edit the Originated By, Domain and Type options used on change cards. Removing an option won't change cards already using it.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <EditableList title="Originated By" items={origins} onChange={setOrigins} testid="picklist-origins" />
          <EditableList title="Domains" items={areas} onChange={setAreas} testid="picklist-areas" />
          <EditableList title="Types" items={types} onChange={setTypes} testid="picklist-types" />
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} data-testid="picklist-cancel"
            className="text-xs font-semibold px-3 py-1.5 rounded border transition-opacity hover:opacity-75" style={{ borderColor: LINE, color: CHARCOAL }}>Cancel</button>
          <button onClick={save} disabled={saving} data-testid="picklist-save"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ background: CHARCOAL, color: "#fff" }}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}


export function ChangeManagementBoard({ onChange }) {
  const [board, setBoard] = useState([]);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState("board"); // board | archive
  const [newTitle, setNewTitle] = useState("");
  const [sweeping, setSweeping] = useState(false);
  const [archiveQuery, setArchiveQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [picklists, setPicklists] = useState({ origins: ORIGINS, areas: AREAS, card_types: CARD_TYPES });
  const [listsOpen, setListsOpen] = useState(false);

  const load = useCallback(async ({ silent } = {}) => {
    try {
      const d = await adminService.listChangeCards();
      setBoard(d.board || []);
      setArchive(d.archive || []);
      if (d.picklists) setPicklists(d.picklists);
      if (!silent) onChange?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [onChange]);

  useEffect(() => { load({ silent: true }); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const move = async (id, body, msg) => {
    try {
      await adminService.updateChangeCard(id, body);
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.message("That card no longer exists — refreshing the board.");
        load();
      } else {
        toast.error(apiError(e));
      }
    }
  };

  const addCard = async () => {
    const t = newTitle.trim();
    if (!t) return;
    try {
      const r = await adminService.createChangeCard({ title: t });
      setNewTitle("");
      await load();
      setEditingId(r.card.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sweep = async () => {
    setSweeping(true);
    try {
      const r = await adminService.sweepChange();
      toast.success(`Swept ${r.swept} card${r.swept === 1 ? "" : "s"} to Archive (In Production)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSweeping(false); }
  };

  const failCard = (id) => {
    const notes = window.prompt("Fail reason / notes (sent back to Build Queue):", "");
    if (notes === null) return;
    move(id, { stage: "build", fail_notes: notes }, "Sent back to Build Queue");
  };

  const rejectCard = (id) => {
    const reason = window.prompt("Reason for rejecting this CR (recorded with your name + timestamp):", "");
    if (reason === null) return;
    move(id, { archive_status: "rejected", reject_reason: reason }, "Archived · Rejected");
  };

  const toggleBuildRequest = (card) => {
    const next = !card.build_requested;
    const kind = card.stage === "analysis" ? "Analysis" : "Build";
    move(card.id, { build_requested: next }, next ? `${kind} requested — tell the agent to "process the green ones"` : `${kind} request cleared`);
  };

  const cardsIn = (stage) => board.filter((c) => c.stage === stage);
  const laneCostTotal = (stage) => cardsIn(stage).reduce((s, c) => s + (c.planned_cost || 0), 0);
  const archIn = (status) => {
    const q = archiveQuery.trim().toLowerCase();
    return archive.filter((c) =>
      c.archive_status === status &&
      (!q || (c.title || "").toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q))
    );
  };

  const visibleArchive = ARCHIVE_GROUPS.flatMap((g) => archIn(g.id));
  const allSelected = visibleArchive.length > 0 && visibleArchive.every((c) => selected.has(c.id));

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleSelectAll = () => setSelected((prev) => {
    if (visibleArchive.length > 0 && visibleArchive.every((c) => prev.has(c.id))) return new Set();
    return new Set(visibleArchive.map((c) => c.id));
  });

  const clearSelection = () => setSelected(new Set());

  const switchView = (v) => { setView(v); clearSelection(); };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} archived card${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const r = await adminService.bulkDeleteChangeCards(ids);
      toast.success(`Deleted ${r.deleted} card${r.deleted === 1 ? "" : "s"}`);
      clearSelection();
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeleting(false); }
  };

  const editItem = (card) => ({ label: "Edit", testid: `change-edit-${card.id}`, icon: <Pencil className="h-3 w-3" />, onClick: () => setEditingId(card.id) });

  const laneMenuItems = (card) => {
    const items = [];
    switch (card.stage) {
      case "new":
        items.push({ label: "To Analysis", testid: `change-to-analysis-${card.id}`, icon: <ArrowRight className="h-3 w-3" />, onClick: () => move(card.id, { stage: "analysis" }, "Moved to Analysis") });
        items.push({ label: "Future", testid: `change-future-${card.id}`, icon: <Clock className="h-3 w-3" />, onClick: () => move(card.id, { archive_status: "future" }, "Archived · Future") });
        items.push({ label: "Reject", testid: `change-reject-${card.id}`, tone: "danger", icon: <X className="h-3 w-3" />, onClick: () => rejectCard(card.id) });
        break;
      case "analysis":
        items.push({ label: "To Triage", testid: `change-to-triage-${card.id}`, icon: <ArrowRight className="h-3 w-3" />, onClick: () => move(card.id, { stage: "triage" }, "Moved to Triage") });
        items.push({ label: "Future", testid: `change-future-${card.id}`, icon: <Clock className="h-3 w-3" />, onClick: () => move(card.id, { archive_status: "future" }, "Archived · Future") });
        items.push({ label: "Reject", testid: `change-reject-${card.id}`, tone: "danger", icon: <X className="h-3 w-3" />, onClick: () => rejectCard(card.id) });
        break;
      case "triage":
        items.push({ label: "To Build/Test", testid: `change-to-build-${card.id}`, icon: <ArrowRight className="h-3 w-3" />, onClick: () => move(card.id, { stage: "build" }, "Moved to Build/Test") });
        items.push({ label: "Future", testid: `change-future-${card.id}`, icon: <Clock className="h-3 w-3" />, onClick: () => move(card.id, { archive_status: "future" }, "Archived · Future") });
        items.push({ label: "Reject", testid: `change-reject-${card.id}`, tone: "danger", icon: <X className="h-3 w-3" />, onClick: () => rejectCard(card.id) });
        break;
      case "build":
        items.push({ label: "To UAT", testid: `change-to-uat-${card.id}`, icon: <ArrowRight className="h-3 w-3" />, onClick: () => move(card.id, { stage: "uat" }, "Moved to UAT & Deploy") }); 
        break;
      case "uat":
        items.push({ label: "Pass", testid: `change-pass-${card.id}`, icon: <Check className="h-3 w-3" />, onClick: () => move(card.id, { stage: "closed", fail_notes: "" }, "Passed → Closed") });
        items.push({ label: "Fail", testid: `change-fail-${card.id}`, tone: "danger", icon: <RotateCcw className="h-3 w-3" />, onClick: () => failCard(card.id) });
        break;
      default:
        break;
    }
    // Edit available in New, Analysis, Triage, Build/Test, and UAT.
    if (["new", "analysis", "triage", "build", "uat"].includes(card.stage)) items.push(editItem(card));
    return items;
  };

  const editingCard = editingId ? [...board, ...archive].find((c) => c.id === editingId) : null;

  if (loading) {
    return <div className="h-full flex items-center justify-center" style={{ color: MUTE }}><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="h-full flex flex-col" data-testid="change-management-board">
      {listsOpen && (
        <PicklistManager origins={picklists.origins} areas={picklists.areas} cardTypes={picklists.card_types}
          onClose={() => setListsOpen(false)}
          onSaved={(pl) => { setPicklists(pl); onChange?.(); }} />
      )}
      {editingCard ? (
        <CardEditor card={editingCard} onClose={() => setEditingId(null)} onChanged={load} areas={picklists.areas} cardTypes={picklists.card_types} origins={picklists.origins} />
      ) : (
      <>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-md border p-0.5" style={{ borderColor: LINE }}>
            <button onClick={() => switchView("board")} data-testid="change-view-board"
              className="text-xs font-semibold px-3 py-1 rounded transition-colors"
              style={view === "board" ? { background: CHARCOAL, color: "#fff" } : { color: MUTE }}>Pipeline</button>
            <button onClick={() => switchView("archive")} data-testid="change-view-archive"
              className="text-xs font-semibold px-3 py-1 rounded transition-colors"
              style={view === "archive" ? { background: CHARCOAL, color: "#fff" } : { color: MUTE }}>Archive</button>
          </div>
          <button onClick={refresh} disabled={refreshing} data-testid="change-refresh-button"
            title="Refresh board"
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border transition-opacity hover:opacity-75 disabled:opacity-50"
            style={{ borderColor: LINE, color: MUTE }}>
            <RotateCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={() => setListsOpen(true)} data-testid="change-manage-lists"
            title="Edit Domain & Type options"
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border transition-opacity hover:opacity-75"
            style={{ borderColor: LINE, color: MUTE }}>
            <Settings2 className="h-3.5 w-3.5" /> Manage lists
          </button>
        </div>
        {view === "board" && (
          <div className="flex items-center gap-2">
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCard()}
              placeholder="New change request…" data-testid="change-new-input"
              className="text-xs bg-white/70 border rounded px-2 py-1.5 w-52 outline-none" style={{ borderColor: LINE, color: CHARCOAL }} />
            <MiniBtn tone="dark" testid="change-new-add" onClick={addCard}><Plus className="h-3 w-3" /> Add</MiniBtn>
          </div>
        )}
      </div>

      {view === "board" ? (
        <div className="flex-1 min-h-0 overflow-x-auto">
          <div className="grid grid-cols-6 gap-2 min-w-[940px] h-full">
            {STAGES.map((s) => (
              <div key={s.id} className="flex flex-col min-h-0 rounded-lg border bg-black/[0.015]" style={{ borderColor: LINE }} data-testid={`change-lane-${s.id}`}>
                <div className="flex items-center justify-between px-2 py-2 border-b" style={{ borderColor: LINE }}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[11px] font-bold uppercase tracking-wide truncate" style={{ color: CHARCOAL }}>{s.label}</span>
                    {s.id === "analysis" && <AnalysisInfo />}
                  </div>
                  {s.id === "closed" ? (
                    <button onClick={sweep} disabled={sweeping || cardsIn("closed").length === 0}
                      data-testid="change-sweep-button"
                      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-1 rounded border disabled:opacity-40 hover:opacity-75 transition-opacity"
                      style={{ color: "#fff", background: CHARCOAL, borderColor: CHARCOAL }}>
                      {sweeping ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />} Sweep
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {["analysis", "triage", "build"].includes(s.id) && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ color: "#8a6d1b", background: "rgba(198,161,91,0.16)" }} data-testid={`lane-ecu-total-${s.id}`}>
                          <Coins className="h-2.5 w-2.5" />{laneCostTotal(s.id)}
                        </span>
                      )}
                      <span className="text-[10px] font-semibold px-1.5 rounded" style={{ color: MUTE }}>{cardsIn(s.id).length}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-1.5">
                  {cardsIn(s.id).map((c) => <Card key={c.id} card={c} menu={<CardMenu items={laneMenuItems(c)} cardId={c.id} />} onRequestBuild={(s.id === "build" || s.id === "analysis") ? () => toggleBuildRequest(c) : undefined} requestKind={s.id === "analysis" ? "analysis" : "build"} />)}
                  {cardsIn(s.id).length === 0 && <p className="text-[10px] text-center py-4" style={{ color: MUTE }}>—</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="change-archive-view">
          <div className="mb-3 flex items-center gap-3 flex-wrap">
            <div className="relative w-full max-w-sm">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: MUTE }} />
              <input
                value={archiveQuery}
                onChange={(e) => setArchiveQuery(e.target.value)}
                placeholder="Search archive…"
                data-testid="archive-search-input"
                className="w-full h-8 pl-8 pr-8 text-xs rounded-md border bg-white/60 outline-none"
                style={{ borderColor: LINE, color: CHARCOAL }}
              />
              {archiveQuery && (
                <button onClick={() => setArchiveQuery("")} data-testid="archive-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2" style={{ color: MUTE }}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold cursor-pointer select-none" style={{ color: MUTE }} data-testid="archive-select-all-label">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                disabled={visibleArchive.length === 0}
                data-testid="archive-select-all"
                className="h-3.5 w-3.5 cursor-pointer"
                style={{ accentColor: CHARCOAL }}
              />
              Select all ({visibleArchive.length})
            </label>
            {selected.size > 0 && (
              <>
                <MiniBtn tone="danger" testid="archive-delete-selected" onClick={deleteSelected}>
                  {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Delete selected ({selected.size})
                </MiniBtn>
                <button onClick={clearSelection} data-testid="archive-clear-selection" className="text-[11px] font-semibold" style={{ color: MUTE }}>Clear</button>
              </>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ARCHIVE_GROUPS.map((g) => (
              <div key={g.id} className="rounded-lg border bg-black/[0.015] p-2" style={{ borderColor: LINE }} data-testid={`archive-group-${g.id}`}>
                <div className="flex items-center justify-between px-1 py-1 mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: CHARCOAL }}>{g.label}</span>
                  <span className="text-[10px] font-semibold" style={{ color: MUTE }}>{archIn(g.id).length}</span>
                </div>
                {archIn(g.id).map((c) => (
                  <Card key={c.id} card={c} selectable selected={selected.has(c.id)} onToggleSelect={() => toggleSelect(c.id)}
                    menu={<CardMenu cardId={c.id} items={[
                      { label: "View details", testid: `archive-view-${c.id}`, icon: <Info className="h-3 w-3" />, onClick: () => setEditingId(c.id) },
                      ...(g.id === "future" ? [{ label: "Send to New", testid: `archive-send-new-${c.id}`, icon: <ArrowRight className="h-3 w-3" />, onClick: () => move(c.id, { stage: "new" }, "Sent to New") }] : []),
                    ]} />} />
                ))}
                {archIn(g.id).length === 0 && <p className="text-[10px] text-center py-4" style={{ color: MUTE }}>Empty</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

export default ChangeManagementBoard;
